import { Field, Select } from '../ui/Field';
import { useResource } from '../../hooks/useResource';
import { useAuth } from '../../auth/AuthProvider';

/**
 * The organisation picker, and the rule about who ever sees one.
 *
 * A platform administrator works across tenants, so anything they create has
 * to say which tenant it belongs to. Everyone else belongs to exactly one
 * organisation and is never asked — the server fills it in from their account
 * and ignores an `orgId` they try to send.
 *
 * The list itself is scoped by `GET /api/orgs`: an organisation administrator
 * asking for it receives their own organisation and nothing else. So this
 * component is not what keeps tenants apart — it is what stops a platform
 * administrator being unable to finish a form, which is what happened before
 * it existed.
 *
 * `useOrgField` returns `{ visible, orgId, setOrgId, ready }`; render `<OrgField>`
 * only when `visible`, and include `orgId` in the request only when it is set.
 */
export function useOrgField() {
  const { user, can } = useAuth();

  // Only a platform administrator can create across organisations, and
  // `org:write` is exactly the permission that says so.
  const visible = can('org:write');
  const orgs = useResource(visible ? '/api/orgs' : null);

  return {
    visible,
    organizations: orgs.data?.organizations || [],
    loading: orgs.loading,
    ownOrgId: user?.orgId || null,
  };
}

export function OrgField({
  value,
  onChange,
  organizations,
  loading,
  required = true,
  label = 'Organisation',
  hint = 'Which tenant this belongs to. Its data stays invisible to every other organisation.',
}) {
  const active = organizations.filter((org) => org.status !== 'SUSPENDED');

  return (
    <Field
      label={label}
      required={required}
      hint={
        loading
          ? 'Loading organisations…'
          : active.length === 0
            ? 'No organisations yet — create one from the Platform page first.'
            : hint
      }
    >
      <Select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={loading ? 'Loading…' : 'Choose an organisation'}
        disabled={loading || active.length === 0}
        options={active.map((org) => ({
          value: org.id,
          label: `${org.name} · ${org.vehicle_count ?? 0} vehicles`,
        }))}
      />
    </Field>
  );
}
